/* eslint-disable @typescript-eslint/no-explicit-any */
import { encode } from 'js-base64';
import { createLog } from './utils';

const AUTH0_ALLOWED_PATTERN = /^[-\w.*~@+ /:]{1,255}$/;
const MAX_LENGTH_VALUE = 255;

type RawExtParams = Record<string, any>;

type SanitizeExtParamsOptions = {
  encodeKeys?: string[];
  specialTruncateKeys?: string[];
};

export const sanitizeExtParams = (
  params: RawExtParams,
  { encodeKeys, specialTruncateKeys }: SanitizeExtParamsOptions = {},
): Record<string, string> => {
  const sanitized: Record<string, string> = {};
  let extCount = 0;

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (!key.startsWith('ext-')) {
      sanitized[key] = value;
      return;
    }

    // Exclude ext- params if authorizationParams has the limit
    if (extCount >= 10) {
      createLog('[Auth0 Params] Too many ext- parameters. Discarding', {
        key,
        safeParams: sanitized,
        value,
      });
      return;
    }

    let strValue = String(value);

    if (encodeKeys?.includes(key)) {
      strValue = encode(strValue).replace(/=/gi, '~');
    } else {
      strValue = strValue.replace(/=/gi, '~');
    }

    // Custom logic for sanitized deviceId and GTM params
    if (specialTruncateKeys?.includes(key)) {
      const rawParts = String(value).split('+');
      let truncatedValue = strValue;

      while (truncatedValue.length > MAX_LENGTH_VALUE && rawParts.length > 1) {
        rawParts.pop();
        truncatedValue = encode(rawParts.join('+')).replace(/=/gi, '~');
      }

      if (AUTH0_ALLOWED_PATTERN.test(truncatedValue)) {
        sanitized[key] = truncatedValue;
        extCount += 1;
      } else {
        createLog('[Auth0 Params] Parameter discarded after truncation. Still invalid.', {
          key,
          originalValue: value,
          safeParams: sanitized,
          transformedValue: truncatedValue,
        });
      }
    } else if (AUTH0_ALLOWED_PATTERN.test(strValue)) {
      sanitized[key] = strValue;
      extCount += 1;
    } else {
      createLog(`[Auth0 Params] Parameter discarded. Invalid value: "${strValue}"`, {
        key,
        originalValue: value,
        safeParams: sanitized,
        transformedValue: strValue,
      });
    }
  });

  return sanitized;
};
